import { ActionIcon, Badge, Group, HoverCard, List, Loader, Menu, Text, ThemeIcon, Tooltip } from '@mantine/core';
import { IconCopy, IconDots, IconPencil, IconRobot, IconTrashX } from '@tabler/icons-react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import { DatasetsInfo } from '@mygpt/app';

import { useGPTStore } from '@/store';

import { SvgDataSets } from './icon';

interface Props {
  info: DatasetsInfo;
  handleEdit: () => void;
  handleDelete: () => void;
  handleClone: () => void;
}

export default ({ handleClone, handleDelete, handleEdit, info }: Props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { assetScope } = useGPTStore();
  const isLocalMode = import.meta.env.VITE_MODE === 'local';
  const isCloudEnterprise = !isLocalMode && !!assetScope;

  const isCloning = useMemo(() => {
    return ['cloning_source', 'cloning_target'].includes(info.data_status);
  }, [info.data_status]);

  /* eslint-disable @typescript-eslint/no-unused-vars */
  const cloneProgress = useMemo(() => {
    if (info.clone_progress.total === 0) return 0;
    return ((info.clone_progress.done / info.clone_progress.total) * 100).toFixed(0);
  }, [info.clone_progress.done, info.clone_progress.total]);

  return (
    <Group className="flex bg-white p-5 rounded-xl hover:bg-violet-100" justify="space-between" gap={20} wrap="nowrap">
      <Group gap={20} wrap="nowrap" className="flex-1">
        <section className="bg-violet-100 p-2 rounded-md">
          <SvgDataSets />
        </section>
        <Group justify="space-between" className="flex-1" wrap="nowrap">
          <div>
            <Tooltip
              label={info.name}
              position="top-start"
              arrowOffset={16}
              multiline
              withArrow
              className="max-w-[500px] break-all"
            >
              <Text lineClamp={1} className="flex-1" w={500}>
                {info.name}
              </Text>
            </Tooltip>
            <Tooltip
              label={info.description}
              position="bottom-start"
              arrowOffset={16}
              multiline
              withArrow
              className="max-w-[500px]  break-all"
            >
              <Text size="sm" c="gray.4" lineClamp={1} className="flex-1" w={500}>
                {info.description}
              </Text>
            </Tooltip>
          </div>
          <div>
            {info.data_status === 'frozen' && (
              <Badge color="yellow" className="flex-none">
                {t('dataset.frozen_tag')}
              </Badge>
            )}
            {info.data_status === 'cloning_target' && (
              <Badge color="green" className="flex-none">
                <Group gap={4} wrap="nowrap">
                  <Loader size={10} color="white" />
                  {t('dataset.generating')}
                  {/* 后端该功能有问题，暂时屏蔽 */}
                  {/* <span>{cloneProgress}%</span> */}
                </Group>
              </Badge>
            )}
            {info.data_status === 'cloning_source' && (
              <Badge color="green" className="flex-none">
                <Group gap={4} wrap="nowrap">
                  <Loader size={10} color="white" />
                  {t('dataset.copying')}
                </Group>
              </Badge>
            )}
          </div>
        </Group>
      </Group>
      {(isLocalMode || isCloudEnterprise ? info.permission_type === 'OWNER' : true) && (
        <Group
          wrap="nowrap"
          gap={20}
          className="flex-none cursor-default"
          onClick={(e) => {
            e.stopPropagation();
          }}
        >
          <HoverCard width={280} shadow="md" disabled={!info.robots.length}>
            <HoverCard.Target>
              <section
                className={`flex items-center rounded-md border-2 border-violet-500 group overflow-hidden ${
                  info.robots.length && 'cursor-pointer'
                }`}
              >
                <span className="bg-violet-500 text-white py-[2px] px-3">{info.robots.length}</span>
                <span className={`flex-none px-3 text-sm ${info.robots.length && 'group-hover:bg-violet-200'}`}>
                  {t('dataset.linked_bot')}
                </span>
              </section>
            </HoverCard.Target>
            <HoverCard.Dropdown>
              <List
                className="max-h-[30vh] overflow-y-auto"
                spacing="xs"
                center
                icon={
                  <ThemeIcon color="violet" size={24} radius="xl">
                    <IconRobot size="1rem" />
                  </ThemeIcon>
                }
              >
                {info.robots.map((item) => {
                  return (
                    <List.Item
                      key={item.id}
                      className="p-2 hover:bg-gray-100 rounded-md cursor-pointer"
                      onClick={() => {
                        navigate(`/bot/${item.id}/data-source`);
                      }}
                    >
                      <Text>{item.name}</Text>
                    </List.Item>
                  );
                })}
              </List>
            </HoverCard.Dropdown>
          </HoverCard>

          <Menu shadow="md">
            <Menu.Target>
              <ActionIcon color="violet" px={2}>
                <IconDots size="2rem" />
              </ActionIcon>
            </Menu.Target>

            <Menu.Dropdown>
              <Menu.Item leftSection={<IconPencil size={14} />} onClick={handleEdit}>
                {t('edit')}
              </Menu.Item>
              <Menu.Item leftSection={<IconCopy size={14} />} onClick={handleClone} disabled={isCloning}>
                {isCloning
                  ? info.data_status === 'cloning_source'
                    ? t('dataset.copying')
                    : t('dataset.generating')
                  : t('clone')}
              </Menu.Item>
              <Menu.Item color="red" leftSection={<IconTrashX size={14} />} onClick={handleDelete}>
                {t('delete_btn')}
              </Menu.Item>
            </Menu.Dropdown>
          </Menu>
        </Group>
      )}
    </Group>
  );
};
